import React, {useMemo, useState} from 'react';
import {Project} from "../../shared/types/types";
import {useElementOnScreen} from "../../shared/hook/useElementOnScreen";
import {Accordion} from "@mantine/core";
import {Carousel} from "@mantine/carousel";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Lightbox from "yet-another-react-lightbox";
import S from './Projects.module.scss';
import {ModalForm} from "../modal";
import { useDisclosure } from '@mantine/hooks';
import {useNavigate} from "react-router-dom";

interface ProjectItemProps {
  project: Project;
}

export const ProjectItem = ({project}: ProjectItemProps) => {
  const [cardRef, isVisible] = useElementOnScreen<HTMLDivElement>();
  const [index, setIndex] = useState(-1);
  const navigate = useNavigate();

  const slides = useMemo(
    () => project.images?.map((src) => ({ src })) || [],
    [project.images]
  );

  const [opened, { open, close }] = useDisclosure(false);
  const [titleModal, setTitleModal] = useState<string>('');
  const [productModal, setProductModal] = useState<string>('');
  const handleOpenModal = (title: string, product?: string) => {
    setTitleModal(title);
    setProductModal(product || "");
    open();
  };

  return (
    <Accordion.Item
      className={`${project.id === '5' ? S.cardBench : S.card} ${isVisible ? S.visible : ''}`}
      ref={cardRef}
      key={project.id}
      value={project.id}
    >
      <Accordion.Control className={S.cardControl}>
        <div className={S.label}>
          <h4 className={S.cardTitle}>
            {project.title}
            {project?.subtitle && <span style={{ color: project.id === '5' ? '#0d9387' : '' }}> {project.subtitle}</span>}
          </h4>
          <p className={S.cardDescription}>
            {project.description}
          </p>
        </div>
      </Accordion.Control>

      <Accordion.Panel>
        <div className={S.panelContent}>
          {project.images && project.images.length > 0 && (
            <Carousel
              classNames={{controls: S.controls, indicator: S.indicator, root: S.carouselRoot}}
              withIndicators
              slideSize={{ base: '100%', sm: '50%', md: '33.333333%' }}
              slideGap={{ base: 0, sm: 'md' }}
              emblaOptions={{ loop: true, align: 'start' }}
              pb={30}
            >
              {project.images?.map((image, index) => (
                <Carousel.Slide
                  key={index}
                  onClick={() => setIndex(index)}
                  className={S.carouselSlide}
                >
                  <img
                    src={image}
                    alt={`${project.title} screenshot ${index + 1}`}
                    className={S.carouselImage}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>
          )}

          <Lightbox
            index={index}
            open={index >= 0}
            close={() => setIndex(-1)}
            slides={slides}
            plugins={[Thumbnails, Zoom]}
            carousel={{
              spacing: "4px",
              padding: "4px",
            }}
            zoom={{
              maxZoomPixelRatio: 3,
              zoomInMultiplier: 2,
              doubleClickDelay: 300,
              doubleTapDelay: 300,
              keyboardMoveDistance: 50,
              wheelZoomDistanceFactor: 100,
              pinchZoomDistanceFactor: 100,
              scrollToZoom: true
            }}
          />

          {project.subDescription && (
            <p className={S.cardDescription}>
              {project.subDescription}
            </p>
          )}

          {project.tags && project.tags.length > 0 && (
            <div className={S.cardTags}>
              {project.tags.map((tag) => (
                <div key={tag.number} className={S.tagItem}>
                  <div className={S.tagHeader}>
                    <div className={S.boxNumber}>{tag.number}</div>
                    <div className={S.paragraphText}>{tag.text}</div>
                  </div>
                  {tag.subItems && tag.subItems.length > 0 && (
                    <div className={S.tagContent}>
                      <ul className={S.subItemsList}>
                        {tag.subItems.map((subItem, idx) => (
                          <li key={idx} className={S.subItem}>
                            {subItem}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {project.effects && project.effects.length > 0 && (
            <div className={S.cardTags}>
              {project.effects.map((tag) => (
                <div key={tag.text} className={S.tagItem}>
                  <div className={S.effectsHeader}>{tag.text}</div>
                  {tag.subItems && tag.subItems.length > 0 && (
                    <div className={S.effects}>
                      <ul className={S.subItemsList}>
                        {tag.subItems.map((subItem, idx) => (
                          <li key={idx} className={S.subItem}>
                            {subItem}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className={S.boxButtons}>
            {project.buttons && project.buttons.length > 0 && (
                <div className={S.buttonsDiv}>
                  {project.buttons.map((button) => (
                      <button
                          className={project?.id === '5' ? S.submitButtonBench : S.submitButton}
                          onClick={() => handleOpenModal(button.text, project?.subtitle || "")}
                          key={button.number}
                      >
                        {button.text}
                      </button>
                  ))}
                </div>
            )}
            {project?.buttonMore && (
                <div
                    className={project?.id === '5' ? S.buttonMoreBench : S.buttonMore}
                    onClick={() => navigate(`/product/${project.buttonMore?.url}`)}
                >
                  {project.buttonMore.text}
                </div>
            )}
          </div>
        </div>
      </Accordion.Panel>
      <ModalForm
          opened={opened}
          close={close}
          title={titleModal}
          product={productModal}
      />
    </Accordion.Item>
  );
};